import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  HealthAndSafety as HealthIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Business as DealIcon,
  Task as TaskIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowIcon,
  Lightbulb as NudgeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { dataHealthApi, type DataHealthDashboard, type ActionableNudge } from '../api/dataHealthApi';

// Score color helper
const getScoreColor = (score: number): string => {
  if (score >= 80) return '#4caf50'; // Green
  if (score >= 60) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

// Grade color helper
const getGradeColor = (grade: string): 'success' | 'warning' | 'error' | 'default' => {
  if (grade === 'A' || grade === 'B') return 'success';
  if (grade === 'C') return 'warning';
  return 'error';
};

// Priority color helper
const getPriorityColor = (priority: string): 'error' | 'warning' | 'info' => {
  if (priority === 'high') return 'error';
  if (priority === 'medium') return 'warning';
  return 'info';
};

interface DataHealthCardProps {
  compact?: boolean;
}

const DataHealthCard: React.FC<DataHealthCardProps> = ({ compact = false }) => {
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery<DataHealthDashboard>({
    queryKey: ['dataHealth', 'dashboard'],
    queryFn: () => dataHealthApi.getDashboard(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refresh every 10 minutes
  });

  if (isLoading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Alert severity="error">Failed to load data health metrics</Alert>
        </CardContent>
      </Card>
    );
  }

  const { score, nudges, stats } = data;

  // Compact version for dashboard sidebar
  if (compact) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HealthIcon color="primary" />
              <Typography variant="h6">Data Health</Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => refetch()}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Score Circle */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <CircularProgress
                variant="determinate"
                value={score.overall_score}
                size={80}
                thickness={6}
                sx={{ color: getScoreColor(score.overall_score) }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                }}
              >
                <Typography variant="h5" fontWeight="bold">
                  {score.overall_score}
                </Typography>
                <Chip
                  label={score.grade}
                  size="small"
                  color={getGradeColor(score.grade)}
                  sx={{ fontSize: '0.7rem', height: 18 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 2 }}>
            <Tooltip title="Overdue Tasks">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color={stats.overdue_tasks > 0 ? 'error' : 'success'}>
                  {stats.overdue_tasks}
                </Typography>
                <Typography variant="caption">Overdue</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Stale Deals">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color={stats.stale_deals_count > 0 ? 'warning.main' : 'success'}>
                  {stats.stale_deals_count}
                </Typography>
                <Typography variant="caption">Stale</Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Forgotten Contacts">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color={stats.forgotten_contacts_count > 0 ? 'warning.main' : 'success'}>
                  {stats.forgotten_contacts_count}
                </Typography>
                <Typography variant="caption">Forgotten</Typography>
              </Box>
            </Tooltip>
          </Box>

          {/* Top Nudge */}
          {nudges.length > 0 && (
            <Box
              sx={{
                bgcolor: 'action.hover',
                borderRadius: 1,
                p: 1.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.selected' },
              }}
              onClick={() => navigate(nudges[0].action_url)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NudgeIcon fontSize="small" color={getPriorityColor(nudges[0].priority)} />
                <Typography variant="body2" fontWeight="medium">
                  {nudges[0].title}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {nudges[0].suggested_action}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full version
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HealthIcon color="primary" fontSize="large" />
            <Typography variant="h5">CRM Data Health</Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          {/* Overall Score */}
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                <CircularProgress
                  variant="determinate"
                  value={score.overall_score}
                  size={120}
                  thickness={6}
                  sx={{ color: getScoreColor(score.overall_score) }}
                />
                <Box
                  sx={{
                    top: 0,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h3" fontWeight="bold">
                    {score.overall_score}
                  </Typography>
                  <Chip
                    label={`Grade ${score.grade}`}
                    color={getGradeColor(score.grade)}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Overall Data Health Score
              </Typography>
            </Box>
          </Grid>

          {/* Score Breakdown */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" gutterBottom>
              Score Breakdown
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Contact Completeness</Typography>
                <Typography variant="body2" fontWeight="medium">{score.contact_completeness}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={score.contact_completeness}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                color={score.contact_completeness >= 70 ? 'success' : 'warning'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Deal Freshness</Typography>
                <Typography variant="body2" fontWeight="medium">{score.deal_freshness}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={score.deal_freshness}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                color={score.deal_freshness >= 70 ? 'success' : 'warning'}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Task Management</Typography>
                <Typography variant="body2" fontWeight="medium">{score.task_management}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={score.task_management}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                color={score.task_management >= 70 ? 'success' : 'warning'}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Activity Tracking</Typography>
                <Typography variant="body2" fontWeight="medium">{score.activity_tracking}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={score.activity_tracking}
                sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200' }}
                color={score.activity_tracking >= 70 ? 'success' : 'warning'}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Actionable Nudges */}
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NudgeIcon color="primary" />
          Action Items
        </Typography>

        {nudges.length === 0 ? (
          <Alert severity="success" icon={<CheckIcon />}>
            Great job! No urgent actions needed. Your CRM data is in good shape!
          </Alert>
        ) : (
          <List dense>
            {nudges.map((nudge) => (
              <ListItem
                key={nudge.id}
                sx={{
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  mb: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
                onClick={() => navigate(nudge.action_url)}
                secondaryAction={
                  <IconButton edge="end" size="small">
                    <ArrowIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {nudge.priority === 'high' ? (
                    <ErrorIcon color="error" />
                  ) : nudge.priority === 'medium' ? (
                    <WarningIcon color="warning" />
                  ) : (
                    <NudgeIcon color="info" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={nudge.title}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={nudge.priority}
                        size="small"
                        color={getPriorityColor(nudge.priority)}
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                      <Typography variant="caption">{nudge.suggested_action}</Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}

        {/* Quick Stats */}
        <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5">{stats.total_deals}</Typography>
            <Typography variant="caption" color="text.secondary">Total Deals</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5">{stats.total_contacts}</Typography>
            <Typography variant="caption" color="text.secondary">Total Contacts</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color={stats.overdue_tasks > 0 ? 'error.main' : 'success.main'}>
              {stats.overdue_tasks}
            </Typography>
            <Typography variant="caption" color="text.secondary">Overdue Tasks</Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" color={stats.stale_deals_count > 0 ? 'warning.main' : 'success.main'}>
              {stats.stale_deals_count}
            </Typography>
            <Typography variant="caption" color="text.secondary">Stale Deals</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DataHealthCard;
